import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../components/layout/AppLayout/AppLayout.jsx';
import ProductTable from '../components/tables/ProductTable.jsx';
import ProductForm from '../components/forms/ProductForm.jsx';
import StockAdjustmentForm from '../components/forms/StockAdjustmentForm.jsx';
import Modal from '../../../components/ui/Modal/Modal.jsx';
import { useProducts } from '../hooks/useProducts.js';

const ProductsPage = React.memo(({ onLogout }) => {
    const { t } = useTranslation(['inventory', 'common']);
    const productsHook = useProducts({
        limitOptions: [10, 20, 50, 100] // Products-specific pagination options
    });
    
    // Modal state
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [showAdjustModal, setShowAdjustModal] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    
    // Form state
    const [formLoading, setFormLoading] = useState(false);

    // Stable callback functions
    const handleAddProduct = useCallback(async () => {
        setEditingProduct(null);
        // Refresh suppliers and categories before opening the modal
        await productsHook.loadCategoriesAndSuppliers();
        setShowCreateForm(true);
    }, [productsHook]);
    
    const handleEditProduct = useCallback(async (product) => {
        setEditingProduct(product);
        // Refresh suppliers and categories before opening the modal
        await productsHook.loadCategoriesAndSuppliers();
        setShowCreateForm(true);
    }, [productsHook]);
    
    const handleFormClose = useCallback(() => {
        setShowCreateForm(false);
        setEditingProduct(null);
    }, []);


    const handleProductSubmit = useCallback(async (productData) => {
        setFormLoading(true);
        productsHook.setError('');

        try {
            let result;
            if (editingProduct) {
                result = await productsHook.updateProduct(editingProduct.id, productData);
            } else {
                result = await productsHook.createProduct(productData);
            }

            if (result.success) {
                handleFormClose();
                productsHook.setError('');
            } else {
                productsHook.setError(result.error);
            }
        } catch (error) {
            console.error('Form submission error:', error);
            productsHook.setError('An unexpected error occurred. Please try again.');
        } finally {
            setFormLoading(false);
        }
    }, [editingProduct, productsHook, handleFormClose]);

    const handleAdjustStock = useCallback((product) => {
        setSelectedProduct(product);
        setShowAdjustModal(true);
    }, []);

    const handleAdjustmentSubmit = useCallback(async (formData) => {
        const result = await productsHook.adjustInventory(selectedProduct.id, formData);
        if (result.success) {
            setShowAdjustModal(false);
            setSelectedProduct(null);
        }
    }, [selectedProduct, productsHook]);

    // Show loading state only for initial load
    if (productsHook.loading && (!productsHook.products || productsHook.products.length === 0)) {
        return (
            <AppLayout 
                title={t('inventory:products')}
                error={productsHook.error}
                setError={productsHook.setError}
                onLogout={onLogout}
            >
                <div className="inventory-container">
                    <div className="loading-state">
                        <div className="spinner"></div>
                        <p>{t('common:messages.loading')}</p>
                    </div>
                </div>
            </AppLayout>
        );
    }

    return (
        <AppLayout 
            title={t('inventory:products')}
            error={productsHook.error}
            setError={productsHook.setError}
            onLogout={onLogout}
        >
            {/* Optimized ProductTable handles all table logic */}
            <ProductTable
                hookData={productsHook}
                onAddProduct={handleAddProduct}
                onEditProduct={handleEditProduct}
                onAdjustStock={handleAdjustStock}
            />

            {/* Product Create/Edit Modal */}
            <Modal
                isOpen={showCreateForm}
                onClose={handleFormClose}
                title={editingProduct ? t('inventory:editProduct') : t('inventory:addProduct')}
                footer={
                    <>
                        <button 
                            type="button" 
                            onClick={handleFormClose} 
                            className="btn btn-secondary"
                            disabled={formLoading}
                        >
                            {t('common:buttons.cancel')}
                        </button>
                        <button 
                            type="submit" 
                            form="product-form"
                            className="btn btn-primary"
                            disabled={formLoading}
                        >
                            {formLoading ? (
                                <>
                                    <span className="spinner-small"></span>
                                    {editingProduct ? t('inventory:updatingProduct') : t('inventory:creatingProduct')}
                                </>
                            ) : (
                                editingProduct ? t('inventory:updateProduct') : t('inventory:createProduct')
                            )}
                        </button>
                    </>
                }
            >
                <ProductForm
                    product={editingProduct}
                    categories={productsHook.categories}
                    suppliers={productsHook.suppliers}
                    onSubmit={handleProductSubmit}
                    loading={formLoading}
                />
            </Modal>

            {/* Stock Adjustment Modal */}
            <Modal
                isOpen={showAdjustModal}
                onClose={() => setShowAdjustModal(false)}
                title={t('inventory:adjust')}
            >
                <StockAdjustmentForm
                    product={selectedProduct}
                    onSubmit={handleAdjustmentSubmit}
                    onCancel={() => setShowAdjustModal(false)}
                    loading={productsHook.actionLoading}
                />
            </Modal>

            {/* Loading overlay for actions */}
            {productsHook.actionLoading && (
                <div className="action-loading-overlay">
                    <div className="spinner"></div>
                </div>
            )}
        </AppLayout>
    );
});

export default ProductsPage;